import { bearingDeg, cardinalIt, distanceM } from '../lib/geo';
import { NETWORKS } from '../lib/operators';
import type { Site } from '../lib/cluster';
import { h, fmtDeg, fmtKm, toast } from './dom';
import { getState, setState } from './state';
import { losFor, profileFor } from './los-loader';
import { renderProfile } from './profile-chart';
import { openSiteForm } from './site-form';
import { deleteSite } from './data';

const RELIABILITY: Record<Site['reliability'], string> = {
  verificato: 'Verificato (catasto CASTEL)',
  manuale: 'Inserito da te',
  stimato: 'Posizione stimata (OpenCelliD)',
};
const LOS_TEXT = {
  libera: 'Visuale libera sul terreno',
  marginale: 'Visuale al limite: il terreno sfiora il fascio',
  ostruita: 'Terreno in mezzo: segnale probabilmente debole',
};

export const radioLabel = (r: string[]) => {
  const out = [];
  if (r.includes('NR')) out.push('5G');
  if (r.includes('LTE')) out.push('4G');
  return out.length ? out.join(' · ') : 'Tecnologia non nota';
};

let current: HTMLElement | null = null;

export function closeSheet() {
  current?.remove();
  current = null;
}

export function openSheet(site: Site) {
  closeSheet();
  const home = getState().settings.home;
  const net = NETWORKS[site.network];
  const dist = home ? distanceM(home, site) : null;
  const brg = home ? bearingDeg(home, site) : null;
  const profileBox = h('div', { class: 'profile-box' }, home ? h('p', { class: 'muted' }, 'Calcolo il profilo del terreno…') : h('p', { class: 'muted' }, 'Imposta la casa per vedere distanza e profilo.'));

  const sheet = h(
    'section',
    { class: 'sheet', role: 'dialog', 'aria-label': `Sito ${site.network}` },
    h('button', { class: 'sheet-close', 'aria-label': 'Chiudi', onclick: closeSheet }, '×'),
    h('div', { class: 'sheet-head' },
      h('span', { class: 'dot', style: `background:${net.color}` }),
      h('div', {},
        h('h2', {}, site.network),
        h('p', { class: 'muted small' }, `Marchi su questa rete: ${net.brands.join(', ')}`),
      ),
    ),
    h('p', { class: `badge rel-${site.reliability}` }, RELIABILITY[site.reliability]),
    site.network === 'Vodafone' && site.source?.startsWith('CASTEL')
      ? h('p', { class: 'muted small' }, 'In CASTEL compare come Fastweb S.p.A.: dal 2025 Fastweb e Vodafone sono la stessa azienda.')
      : null,
    dist !== null && brg !== null
      ? h('div', { class: 'big-numbers' },
          h('div', {}, h('strong', {}, fmtKm(dist)), h('span', {}, 'distanza')),
          h('div', {}, h('strong', {}, `${fmtDeg(brg)} ${cardinalIt(brg)}`), h('span', {}, 'direzione dalla casa')),
        )
      : null,
    h('p', {}, radioLabel(site.radios)),
    site.name ? h('p', {}, site.name) : null,
    site.note ? h('p', { class: 'muted small' }, site.note) : null,
    profileBox,
    h('div', { class: 'actions' },
      h('button', { class: 'primary', onclick: () => { setState({ selectedSiteId: site.id, tab: 'posto' }); closeSheet(); } }, 'Punta qui'),
      site.reliability !== 'stimato'
        ? h('button', { onclick: () => { closeSheet(); openSiteForm(site); } }, 'Modifica')
        : null,
      site.reliability !== 'stimato'
        ? h('button', {
            class: 'danger',
            onclick: async () => {
              if (!confirm('Eliminare questo sito?')) return;
              try { await deleteSite(site.id); closeSheet(); } catch (e) { toast((e as Error).message); }
            },
          }, 'Elimina')
        : null,
    ),
    h('details', {},
      h('summary', {}, 'Dettagli tecnici'),
      h('p', { class: 'small mono' }, `${site.lat.toFixed(5)}, ${site.lon.toFixed(5)}`),
      site.source ? h('p', { class: 'small' }, `Fonte: ${site.source}${site.checkedAt ? ` · ${site.checkedAt}` : ''}`) : null,
      h('p', { class: 'small' }, h('a', { href: `https://lteitaly.it/`, target: '_blank', rel: 'noopener' }, 'Apri LTEItaly'), ' per confrontare.'),
    ),
  );
  document.body.append(sheet);
  current = sheet;

  if (home && dist !== null) {
    losFor(site).then(r => {
      if (current !== sheet) return;
      profileBox.replaceChildren();
      const ground = profileFor(site);
      if (!r || !ground) {
        profileBox.append(h('p', { class: 'muted' }, 'Profilo non disponibile (servizio altimetrico non raggiungibile).'));
        return;
      }
      profileBox.append(h('p', { class: `los los-${r.status}` }, LOS_TEXT[r.status]));
      renderProfile(profileBox, ground, r, dist);
      profileBox.append(h('p', { class: 'muted small' }, 'In pianura edifici, capannoni e alberi non sono nel profilo: contano le prove sul posto.'));
    });
  }
}
